import { clsx, type ClassValue } from 'clsx';
import ExifReader from 'exifreader';
import moment from 'moment';
import { twMerge } from 'tailwind-merge';
import { v4 as uuidv4 } from 'uuid';

import { FileFormat, FileUploadDTO } from '@/types/api-types';

import {
  ACCEPTED_FILE_TYPES,
  FILE_UPLOAD_STATUS,
} from '@/constants/global-constants';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

export interface ExifData {
  // Image Details
  width?: number;
  height?: number;
  orientation?: number;
  format?: string;
  colorSpace?: string;
  compression?: string;
  bitDepth?: number;
  dpiX?: number;
  dpiY?: number;
  resolutionUnit?: string;

  // Date/Time
  dateTimeOriginal?: string;
  dateTimeModified?: string;
  dateTimeDigitized?: string;

  // Location Data
  latitude?: number;
  longitude?: number;
  altitude?: number;
  gpsTimeStamp?: string;
  gpsDateStamp?: string;
  gpsSpeed?: number;
  gpsSpeedRef?: string;
  gpsImgDirection?: number;
  gpsImgDirectionRef?: string;

  // Camera/Device Info
  make?: string;
  model?: string;
  software?: string;
  serialNumber?: string;
  lensModel?: string;

  // Camera Settings
  exposureTime?: string;
  fNumber?: number;
  iso?: number;
  focalLength?: number;
  exposureProgram?: string;
  meteringMode?: string;
  flash?: string;
  whiteBalance?: string;
  apertureValue?: number;
  shutterSpeed?: string;
  exposureBias?: number;
  brightness?: number;
  subjectDistance?: number;

  // Image Description
  description?: string;
  userComment?: string;
  title?: string;
  subject?: string;
  rating?: number;

  // Rights
  artist?: string;
  copyright?: string;
  creator?: string;
  creatorTool?: string;

  // Thumbnail Data
  thumbnail?: {
    width?: number;
    height?: number;
    image?: Uint8Array;
    base64?: string;
  };
}

export async function extractExifData(file: File): Promise<ExifData> {
  try {
    const tags = await ExifReader.load(file, {
      expanded: true,
      includeUnknown: true,
    });

    const exif = tags?.exif;
    const ifd0 = (tags as any)?.['ifd0'] || (tags as any)?.tiff || {};
    const gps = tags?.gps;
    const xmp = (tags as any)?.xmp || {};
    const iptc = (tags as any)?.iptc || {};
    const quicktime = (tags as any)?.quicktime || {};
    const thumbnailData = tags?.Thumbnail;

    // Extract GPS coordinates with proper conversion
    const latitude = gps?.Latitude
      ? convertGPSToDecimal(gps.Latitude, (gps as any)?.LatitudeRef)
      : undefined;
    const longitude = gps?.Longitude
      ? convertGPSToDecimal(gps.Longitude, (gps as any)?.LongitudeRef)
      : undefined;
    const altitude = gps?.Altitude
      ? (gps as any)?.AltitudeRef === 1
        ? -gps.Altitude
        : gps.Altitude
      : undefined;

    const thumbnail = thumbnailData
      ? {
          width: thumbnailData.ImageWidth?.value,
          height: thumbnailData.ImageLength?.value,
          image: new Uint8Array(thumbnailData.image as ArrayBuffer),
          base64: thumbnailData.base64,
        }
      : undefined;

    // Helper function to safely get string value from potentially array or undefined
    const getStringValue = (
      value: string | string[] | undefined,
    ): string | undefined => {
      if (Array.isArray(value)) return value[0];
      return value;
    };

    // Helper function to safely get EXIF value
    const getExifValue = (obj: any, field: string): string | undefined => {
      const value = obj?.[field];
      return value?.description || value?.value;
    };

    // Prefer robust date extraction across multiple namespaces
    const readRawDate = (candidates: Array<any>): string | undefined => {
      for (const candidate of candidates) {
        if (!candidate) continue;
        const raw = candidate?.description ?? candidate?.value ?? candidate;
        if (raw == null) continue;
        const str = Array.isArray(raw) ? String(raw[0]) : String(raw);
        const normalized = formatExifDate(str);
        if (normalized) return normalized;
      }
      return undefined;
    };

    const dateTimeOriginal = readRawDate([
      exif?.DateTimeOriginal,
      (exif as any)?.CreateDate,
      (ifd0 as any)?.DateTimeOriginal,
      xmp?.DateTimeOriginal,
      xmp?.CreateDate,
      xmp?.DateCreated,
      iptc?.DateCreated && iptc?.TimeCreated
        ? `${iptc.DateCreated?.description || iptc.DateCreated?.value} ${
            iptc.TimeCreated?.description || iptc.TimeCreated?.value
          }`
        : undefined,
      quicktime?.CreateDate,
    ]);

    const dateTimeModified = readRawDate([
      exif?.DateTime,
      (ifd0 as any)?.DateTime,
      xmp?.ModifyDate,
    ]);

    const dateTimeDigitized = readRawDate([
      exif?.DateTimeDigitized,
      xmp?.MetadataDate,
    ]);

    return {
      // Image Details
      width:
        tags?.file?.['Image Width']?.value ||
        tags?.png?.['Image Width']?.value ||
        tags?.pngFile?.['Image Width']?.value ||
        exif?.PixelXDimension?.value,
      height:
        tags?.file?.['Image Height']?.value ||
        tags?.png?.['Image Height']?.value ||
        tags?.pngFile?.['Image Height']?.value ||
        exif?.PixelYDimension?.value,
      orientation: exif?.Orientation?.value,
      format: (tags?.file as any)?.['File Type']?.value,
      colorSpace: exif?.ColorSpace?.description,
      compression: exif?.Compression?.description,
      bitDepth: (tags?.file as any)?.['Bit Depth']?.value,
      dpiX:
        (ifd0 as any)?.XResolution?.value ||
        (tags as any)?.file?.['X Resolution']?.value,
      dpiY:
        (ifd0 as any)?.YResolution?.value ||
        (tags as any)?.file?.['Y Resolution']?.value,
      resolutionUnit:
        (ifd0 as any)?.ResolutionUnit?.description ||
        (tags as any)?.file?.['Resolution Unit']?.description,

      // Date/Time
      dateTimeOriginal,
      dateTimeModified,
      dateTimeDigitized,

      // Location Data
      latitude,
      longitude,
      altitude,
      gpsTimeStamp: (gps as any)?.TimeStamp?.description,
      gpsDateStamp: (gps as any)?.DateStamp?.description,
      gpsSpeed: (gps as any)?.Speed?.value,
      gpsSpeedRef: (gps as any)?.SpeedRef?.description,
      gpsImgDirection: (gps as any)?.ImgDirection?.value,
      gpsImgDirectionRef: (gps as any)?.ImgDirectionRef?.description,

      // Camera/Device Info
      make: getExifValue(ifd0, 'Make'),
      model: getExifValue(ifd0, 'Model'),
      software: getExifValue(ifd0, 'Software'),
      serialNumber: (exif as any)?.BodySerialNumber?.value,
      lensModel: getStringValue((exif as any)?.LensModel?.value),

      // Camera Settings
      exposureTime: exif?.ExposureTime?.description,
      fNumber: Array.isArray(exif?.FNumber?.value)
        ? exif?.FNumber?.value[0]
        : exif?.FNumber?.value,
      iso: Array.isArray(exif?.ISOSpeedRatings?.value)
        ? exif?.ISOSpeedRatings?.value[0]
        : exif?.ISOSpeedRatings?.value,
      focalLength: Array.isArray(exif?.FocalLength?.value)
        ? exif?.FocalLength?.value[0]
        : exif?.FocalLength?.value,
      exposureProgram: exif?.ExposureProgram?.description,
      meteringMode: exif?.MeteringMode?.description,
      flash: exif?.Flash?.description,
      whiteBalance: exif?.WhiteBalance?.description,
      apertureValue: Array.isArray((exif as any)?.ApertureValue?.value)
        ? (exif as any)?.ApertureValue?.value[0]
        : (exif as any)?.ApertureValue?.value,
      shutterSpeed: (exif as any)?.ShutterSpeedValue?.description,
      exposureBias: Array.isArray((exif as any)?.ExposureBiasValue?.value)
        ? (exif as any)?.ExposureBiasValue?.value[0]
        : (exif as any)?.ExposureBiasValue?.value,
      brightness: Array.isArray((exif as any)?.BrightnessValue?.value)
        ? (exif as any)?.BrightnessValue?.value[0]
        : (exif as any)?.BrightnessValue?.value,
      subjectDistance: Array.isArray((exif as any)?.SubjectDistance?.value)
        ? (exif as any)?.SubjectDistance?.value[0]
        : (exif as any)?.SubjectDistance?.value,

      // Image Description
      description: getExifValue(ifd0, 'ImageDescription'),
      userComment:
        typeof exif?.UserComment === 'object' &&
        exif?.UserComment !== null &&
        'value' in exif.UserComment
          ? String(exif.UserComment.value)
          : undefined,
      title: getStringValue(ifd0?.DocumentName?.value),
      subject: getStringValue(ifd0?.Subject?.value),
      rating: ifd0?.Rating?.value,

      // Rights
      artist: getExifValue(ifd0, 'Artist'),
      copyright: getExifValue(ifd0, 'Copyright'),
      creator: getStringValue(ifd0?.Creator?.value),
      creatorTool: getStringValue(ifd0?.CreatorTool?.value),

      // Thumbnail
      thumbnail,
    };
  } catch (error) {
    return {};
  }
}

// Helper function to convert GPS coordinates to decimal format
function convertGPSToDecimal(coord: any, ref: any): number | undefined {
  if (!coord || !ref) return undefined;

  let decimal = 0;
  if (Array.isArray(coord)) {
    decimal = coord[0] + coord[1] / 60 + coord[2] / 3600;
  } else {
    decimal = coord;
  }

  if (ref === 'S' || ref === 'W') {
    decimal = -decimal;
  }

  return decimal;
}

// Helper function to format EXIF dates consistently
function formatExifDate(date: string | undefined): string | undefined {
  if (!date) return undefined;

  const trimmed = String(date).trim();

  const formats = [
    'YYYY:MM:DD HH:mm:ss',
    'YYYY:MM:DD HH:mm:ss.SSS',
    'YYYY:MM:DD HH:mm:ssZ',
    'YYYY:MM:DD HH:mm:ss.SSSZ',
    'YYYY:MM:DDTHH:mm:ss',
    'YYYY:MM:DDTHH:mm:ssZ',
    'YYYY:MM:DDTHH:mm:ss.SSS',
    'YYYY:MM:DDTHH:mm:ss.SSSZ',
    'YYYY-MM-DD HH:mm:ss',
    'YYYY-MM-DDTHH:mm:ss',
    'YYYY-MM-DDTHH:mm:ssZ',
    'YYYY-MM-DDTHH:mm:ss.SSS',
    'YYYY-MM-DDTHH:mm:ss.SSSZ',
    'YYYY/MM/DD HH:mm:ss',
    'YYYY/MM/DDTHH:mm:ss',
    'DD:MM:YYYY HH:mm:ss',
  ];

  for (const format of formats) {
    const parsed = moment(trimmed, format, true);
    if (parsed.isValid()) {
      return parsed.utcOffset(0, true).format('YYYY:MM:DD HH:mm:ss');
    }
  }

  const loose = moment(trimmed);
  if (loose.isValid()) {
    return loose.utcOffset(0, true).format('YYYY:MM:DD HH:mm:ss');
  }

  const iptcMatch = trimmed.match(/^(\d{8})[ T]?(\d{6})(?:([+-]\d{4}))?$/);
  if (iptcMatch) {
    const [_, d, t, tz] = iptcMatch;
    const iso = `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}T${t.slice(
      0,
      2,
    )}:${t.slice(2, 4)}:${t.slice(4, 6)}${tz || ''}`;
    const parsed = moment.parseZone(iso);
    if (parsed.isValid()) {
      return parsed.utc().format('YYYY:MM:DD HH:mm:ss');
    }
  }

  return undefined;
}

// src/utils/fileMetadata.ts
export interface FileMetadata {
  type: string;
  size: number;
  lastModified: number;
  name: string;
  extension: string;
  dimensions?: { width: number; height: number };
  duration?: number;
}

export async function parseFileMetadata(file: File): Promise<FileMetadata> {
  const metadata: FileMetadata = {
    type: file.type,
    size: file.size,
    lastModified: file.lastModified,
    name: file.name,
    extension: file.name.split('.').pop() || '',
  };

  if (file.type.startsWith('image/')) {
    const dimensions = await getImageDimensions(file);
    metadata.dimensions = dimensions;
  }

  if (file.type.startsWith('video/')) {
    const duration = await getVideoDuration(file);
    metadata.duration = duration;
  }

  return metadata;
}

function getImageDimensions(
  file: File,
): Promise<{ width: number; height: number }> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      resolve({ width: img.width, height: img.height });
    };
    img.src = URL.createObjectURL(file);
  });
}

function getVideoDuration(file: File): Promise<number> {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(video.src);
      resolve(video.duration);
    };
    video.src = URL.createObjectURL(file);
  });
}

// File Upload Helper Functions
export const getFileCreationDate = (exifData: ExifData, file: File): Date => {
  // Try EXIF creation date first
  if (exifData.dateTimeOriginal) {
    return moment(exifData.dateTimeOriginal, 'YYYY:MM:DD HH:mm:ss').toDate();
  }

  return new Date(file.lastModified);
};

export const isFileCreatedWithinLimit = (
  creationDate: Date,
  daysLimit: number,
): boolean => {
  if (!creationDate) return false;

  const daysInMs = daysLimit * 24 * 60 * 60 * 1000;
  const cutoffDate = new Date(Date.now() - daysInMs);

  return creationDate >= cutoffDate;
};

export const getFileError = (
  exifData: ExifData,
  file: File,
  daysLimit: number,
  settings?: {
    check_metadata?: boolean;
    min_resolution?: string;
    max_resolution?: string;
    max_shooting_date_days?: number;
    allowed_formats?: FileFormat[];
  },
): string | undefined => {
  if (!settings?.check_metadata) {
    return undefined;
  }

  const creationDate = getFileCreationDate(exifData, file);
  const fileType = file.type.toLowerCase();
  const fileExt = file.name.split('.').pop()?.toLowerCase();
  // Determine if file is an image or video
  const isImage = fileType.startsWith('image/');
  const isVideo = fileType.startsWith('video/');
  const isImageOrVideo = isImage || isVideo;

  // Check upload date limit
  if (isImage && !isFileCreatedWithinLimit(creationDate, daysLimit)) {
    return `Файл створений більше ${daysLimit} днів тому.`;
  }

  // Enhanced metadata validation for images
  if (settings?.check_metadata && isImage && fileExt !== 'png') {
    const requiredMetadata = ['dateTimeOriginal', 'width', 'height'];
    const missingMetadata = requiredMetadata.filter(
      (field) => !exifData[field as keyof ExifData],
    );

    if (missingMetadata.length > 0) {
      return `Файл не містить обов'язкових метаданих: ${missingMetadata.join(', ')}.`;
    }
  }

  if (
    !!fileExt &&
    settings?.allowed_formats &&
    !settings.allowed_formats.some(
      (format) =>
        (format.id === fileExt || format.label === fileExt) && format.enabled,
    )
  ) {
    return `Файл має неприпустимий формат: ${fileExt}.`;
  }

  // Check shooting date for images and videos
  if (
    settings?.max_shooting_date_days &&
    isImageOrVideo &&
    exifData.dateTimeOriginal
  ) {
    const shootingDate = moment(
      exifData.dateTimeOriginal,
      'YYYY:MM:DD HH:mm:ss',
    ).toDate();
    if (
      !isFileCreatedWithinLimit(shootingDate, settings.max_shooting_date_days)
    ) {
      return `Фото зроблено більше ${settings.max_shooting_date_days} днів тому.`;
    }
  }

  // Enhanced resolution checking
  if (
    isImageOrVideo &&
    (settings?.min_resolution || settings?.max_resolution) &&
    exifData.width &&
    exifData.height
  ) {
    const currentResolution = `${exifData.width}x${exifData.height}`;

    if (settings.min_resolution) {
      const [minWidth, minHeight] = settings.min_resolution
        .split('x')
        .map(Number);
      if (
        !minWidth ||
        !minHeight ||
        exifData.width < minWidth ||
        exifData.height < minHeight
      ) {
        return `Роздільна здатність файлу (${currentResolution}) менша за мінімальну (${settings.min_resolution}).`;
      }
    }

    if (settings.max_resolution) {
      const [maxWidth, maxHeight] = settings.max_resolution
        .split('x')
        .map(Number);
      if (!maxWidth || !maxHeight) {
        return `Некоректний формат максимальної роздільної здатності (${settings.max_resolution}).`;
      }
      if (exifData.width > maxWidth || exifData.height > maxHeight) {
        return `Роздільна здатність файлу (${currentResolution}) більша за максимальну (${maxWidth}x${maxHeight}).`;
      }
    }

    // Check aspect ratio is not extremely skewed
    const aspectRatio = exifData.width / exifData.height;
    if (aspectRatio > 5 || aspectRatio < 0.2) {
      return 'Співвідношення сторін зображення занадто спотворене.';
    }
  }

  return undefined;
};

/**
 * Collects ALL validation errors for a file (not just the first one).
 * Used to store all errors when uploading with "ignore date errors" checkbox.
 */
export const getFileErrors = (
  exifData: ExifData,
  file: File,
  daysLimit: number,
  settings?: {
    check_metadata?: boolean;
    min_resolution?: string;
    max_resolution?: string;
    max_shooting_date_days?: number;
    allowed_formats?: FileFormat[];
  },
): string[] => {
  const errors: string[] = [];

  if (!settings?.check_metadata) {
    return errors;
  }

  const creationDate = getFileCreationDate(exifData, file);
  const fileType = file.type.toLowerCase();
  const fileExt = file.name.split('.').pop()?.toLowerCase();
  const isImage = fileType.startsWith('image/');
  const isVideo = fileType.startsWith('video/');
  const isImageOrVideo = isImage || isVideo;

  // Check upload date limit
  if (isImage && !isFileCreatedWithinLimit(creationDate, daysLimit)) {
    errors.push(`Файл створений більше ${daysLimit} днів тому.`);
  }

  // Check shooting date for images and videos
  if (
    settings?.max_shooting_date_days &&
    isImageOrVideo &&
    exifData.dateTimeOriginal
  ) {
    const shootingDate = moment(
      exifData.dateTimeOriginal,
      'YYYY:MM:DD HH:mm:ss',
    ).toDate();
    if (
      !isFileCreatedWithinLimit(shootingDate, settings.max_shooting_date_days)
    ) {
      errors.push(
        `Фото зроблено більше ${settings.max_shooting_date_days} днів тому.`,
      );
    }
  }

  // Enhanced metadata validation for images
  if (settings?.check_metadata && isImage && fileExt !== 'png') {
    const requiredMetadata = ['dateTimeOriginal', 'width', 'height'];
    const missingMetadata = requiredMetadata.filter(
      (field) => !exifData[field as keyof ExifData],
    );

    if (missingMetadata.length > 0) {
      errors.push(
        `Файл не містить обов'язкових метаданих: ${missingMetadata.join(', ')}.`,
      );
    }
  }

  // Check allowed formats
  if (
    !!fileExt &&
    settings?.allowed_formats &&
    !settings.allowed_formats.some(
      (format) =>
        (format.id === fileExt || format.label === fileExt) && format.enabled,
    )
  ) {
    errors.push(`Файл має неприпустимий формат: ${fileExt}.`);
  }

  // Enhanced resolution checking
  if (
    isImageOrVideo &&
    (settings?.min_resolution || settings?.max_resolution) &&
    exifData.width &&
    exifData.height
  ) {
    const currentResolution = `${exifData.width}x${exifData.height}`;

    if (settings.min_resolution) {
      const [minWidth, minHeight] = settings.min_resolution
        .split('x')
        .map(Number);
      if (
        !minWidth ||
        !minHeight ||
        exifData.width < minWidth ||
        exifData.height < minHeight
      ) {
        errors.push(
          `Роздільна здатність файлу (${currentResolution}) менша за мінімальну (${settings.min_resolution}).`,
        );
      }
    }

    if (settings.max_resolution) {
      const [maxWidth, maxHeight] = settings.max_resolution
        .split('x')
        .map(Number);
      if (!maxWidth || !maxHeight) {
        errors.push(
          `Некоректний формат максимальної роздільної здатності (${settings.max_resolution}).`,
        );
      } else if (exifData.width > maxWidth || exifData.height > maxHeight) {
        errors.push(
          `Роздільна здатність файлу (${currentResolution}) більша за максимальну (${maxWidth}x${maxHeight}).`,
        );
      }
    }

    // Check aspect ratio is not extremely skewed
    const aspectRatio = exifData.width / exifData.height;
    if (aspectRatio > 5 || aspectRatio < 0.2) {
      errors.push('Співвідношення сторін зображення занадто спотворене.');
    }
  }

  return errors;
};

export const mapFileToDTO = async (
  file: File,
  agreementUuid: string,
  daysLimit: number,
  userUuid: string,
  validationSettings?: {
    check_metadata?: boolean;
    min_resolution?: string;
    max_resolution?: string;
    max_shooting_date_days?: number;
    allowed_formats?: FileFormat[];
  },
): Promise<FileUploadDTO> => {
  //check file type by extension
  let fileType = '';
  const fileFormat = file.name.split('.').pop()!.toLowerCase();
  Object.entries(ACCEPTED_FILE_TYPES).forEach(([key, value]) => {
    if (value.some((ext) => ext.includes(fileFormat))) {
      fileType = key;
    }
  });
  const isImageOrVideo =
    file.type.startsWith('image/') || file.type.startsWith('video/');
  const exifFileData = isImageOrVideo ? await extractExifData(file) : {};

  // if (fileFormat === 'heic') {
  //   file = await convertHeicToPng(file);
  // }

  const creationDate = getFileCreationDate(exifFileData, file);

  const error = getFileError(exifFileData, file, daysLimit, validationSettings);
  const errors = getFileErrors(
    exifFileData,
    file,
    daysLimit,
    validationSettings,
  );
  return {
    error,
    errors, // All validation errors collected
    file_data: file,
    uuid: uuidv4(),
    file_link: '',
    thumbnail_url: '',
    webp_url: null, // Will be populated during upload if image
    validation_errors: [], // Will be populated when uploading with "ignore date errors"
    blob_name: '',
    progress: 0,
    status: error
      ? FILE_UPLOAD_STATUS.UPLOAD_FAILED
      : FILE_UPLOAD_STATUS.PENDING,
    name: file.name,
    size: formatFileSize(file.size),
    file_type: fileType,
    created: creationDate.toISOString(),
    modified: exifFileData.dateTimeModified
      ? moment(
          exifFileData.dateTimeModified,
          'YYYY:MM:DD HH:mm:ss',
        ).toISOString()
      : creationDate.toISOString(),
    agreement: agreementUuid,
    format: fileFormat,
    gps_longitude: exifFileData.longitude?.toString() || '',
    gps_latitude: exifFileData.latitude?.toString() || '',
    description: exifFileData.description || '',
    height: exifFileData.height?.toString() || '',
    width: exifFileData.width?.toString() || '',
    filmed_date: exifFileData.dateTimeOriginal
      ? moment(exifFileData.dateTimeOriginal, 'YYYY:MM:DD HH:mm:ss').toDate()
      : creationDate,
    upload_date: new Date(),
    user: userUuid,
  };
};

export const getFileExtension = (file: File): string => {
  return file.name.split('.').pop()!.toLowerCase();
};

export const getFileExtensionFromString = (fileUrl: string): string => {
  return fileUrl.split('.').pop()!.toLowerCase();
};

// Error type classification for file upload validation
export type FileErrorType = 'date' | 'other';

/**
 * Classifies file validation error as date-related or other.
 * Date-related errors include:
 * - Upload date errors: "Файл створений більше X днів тому."
 * - Shooting date errors: "Фото зроблено більше X днів тому."
 */
export const getFileErrorType = (error: string): FileErrorType => {
  if (error.includes('днів тому')) {
    return 'date';
  }
  return 'other';
};
